import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client/react';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import {
  FOLLOW_CHANNEL,
  UNFOLLOW_CHANNEL,
  type FollowChannelData,
  type FollowChannelVariables,
  type UnfollowChannelData,
  type UnfollowChannelVariables,
} from '@jwp/ott-common/src/graphql/mutation/channel.mutations';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useFollowChannelStore } from '@jwp/ott-common/src/stores/FollowChannelStore';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';

const getErrorMessage = (err: unknown, fallback: string) => {
  const graphqlError = err as { graphQLErrors?: Array<{ message?: string }>; message?: string };
  return graphqlError?.graphQLErrors?.[0]?.message || graphqlError?.message || fallback;
};

export const useFollowChannel = (chId?: string | null) => {
  const { t } = useTranslation(['user', 'common']);
  const navigate = useNavigate();
  const userId = useAccountStore((state) => state.user?.id);
  const isFollowing = useFollowChannelStore((state) => (chId ? !!state.statusByChannelId[chId] : false));
  const isBusy = useFollowChannelStore((state) => (chId ? !!state.pendingByChannelId[chId] : false));
  const hydrate = useFollowChannelStore((state) => state.hydrate);
  const setStatus = useFollowChannelStore((state) => state.setStatus);
  const setPending = useFollowChannelStore((state) => state.setPending);
  const reset = useFollowChannelStore((state) => state.reset);
  const previousUserIdRef = useRef(userId);

  const [followChannel] = useMutation<FollowChannelData, FollowChannelVariables>(FOLLOW_CHANNEL, {
    client: getAuthApolloClient(),
  });
  const [unfollowChannel] = useMutation<UnfollowChannelData, UnfollowChannelVariables>(UNFOLLOW_CHANNEL, {
    client: getAuthApolloClient(),
  });

  useEffect(() => {
    if (previousUserIdRef.current && !userId) {
      reset();
    }
    previousUserIdRef.current = userId;
  }, [reset, userId]);

  const toggleFollow = useCallback(async () => {
    if (!userId) {
      navigate('/login');
      return;
    }

    if (!chId || useFollowChannelStore.getState().pendingByChannelId[chId]) {
      return;
    }

    const previousIsFollowing = !!useFollowChannelStore.getState().statusByChannelId[chId];
    const nextIsFollowing = !previousIsFollowing;

    setPending(chId, true);
    setStatus(chId, nextIsFollowing);

    try {
      if (previousIsFollowing) {
        const response = await unfollowChannel({ variables: { chId } });
        if (response.error) {
          throw response.error;
        }
        const next = response.data?.unfollowChannel?.isFollowing;
        setStatus(chId, typeof next === 'boolean' ? next : false);
      } else {
        const response = await followChannel({ variables: { chId } });
        if (response.error) {
          throw response.error;
        }
        const next = response.data?.followChannel?.isFollowing;
        setStatus(chId, typeof next === 'boolean' ? next : true);
      }
    } catch (err: unknown) {
      setStatus(chId, previousIsFollowing);
      showToast(
        getErrorMessage(
          err,
          t('common:something_went_wrong', { defaultValue: 'Something went wrong. Please try again.' }),
        ),
        'error',
      );
    } finally {
      setPending(chId, false);
    }
  }, [chId, followChannel, navigate, setPending, setStatus, t, unfollowChannel, userId]);

  return {
    isFollowing,
    isBusy,
    toggleFollow,
    hydrate,
  };
};
